/**
 * CR-6451: supplier-view layout for the Quality → NCR filter panel.
 *
 * Renders exactly the controls in the supplied design, grouped into its four
 * sections — Date Range · Company · Supplier · NCR Details — with the same
 * section shell (`FilterSection` + divider) and control density
 * (`deliveryFilterStyles`) as the Deliveries tab, the accepted reference for
 * every supplier-view filter rework. Controls the design omits (Search,
 * Classification, Claim Status, Action Status, Collection Date) remain on the
 * customer view only.
 *
 * Every control keeps its input type. Supplier Group narrows Supplier Site
 * (existing cascade) and so does the new Supplier Country. Column widths
 * follow the design per section rather than the Deliveries four-up grid.
 */
import { Calendar, Building2, Truck, FileText } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { FilterSection } from '../filters/FilterSection';
import {
  inputCls, labelCls, dateWrap, dateInputCls, gridCls,
} from '../purchasing/tabs/delivery/deliveryFilterStyles';
import type { NCRFiltersState } from './ncrFilterParams';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';

/** Use consistent 4-column responsive grid like customer view for all sections. */
const fourUpCls = 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface Option { value: string; label: string }

export interface NCRFilterSupplierLayoutProps {
  values: NCRFiltersState;
  onChange: (next: Partial<NCRFiltersState>) => void;
  quickRanges: ReadonlyArray<{ id: string; label: string }>;
  activeQuickRange: string | null;
  onQuickRange: (id: string) => void;
  siteOptions: Option[];
  warehouseOptions: Option[];
  supplierCountryOptions: Option[];
  supplierGroupOptions: Option[];
  /** Already narrowed by Supplier Group and Supplier Country. */
  supplierSiteOptions: Option[];
  statusOptions: ReadonlyArray<Option>;
  liabilityOptions: ReadonlyArray<Option>;
  /** Enter in a text field applies the filters. */
  onApply?: () => void;
}

const REQUIREMENTS: Array<{ key: keyof NCRFiltersState; label: string }> = [
  { key: 'creditRequired', label: 'Credit required' },
  { key: 'eightDRequired', label: '8D required' },
  { key: 'collectionRequired', label: 'Collection required' },
  { key: 'replacementRequired', label: 'Replacement required' },
];

export function NCRFilterSupplierLayout({
  values, onChange, quickRanges, activeQuickRange, onQuickRange,
  siteOptions, warehouseOptions, supplierCountryOptions, supplierGroupOptions,
  supplierSiteOptions, statusOptions, liabilityOptions, onApply,
}: NCRFilterSupplierLayoutProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const dateField = (label: string, key: 'dateFrom' | 'dateTo') => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className={dateWrap}>
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={values[key]}
          onChange={(e) => onChange({ [key]: e.target.value } as Partial<NCRFiltersState>)}
          className={dateInputCls}
        />
      </div>
    </div>
  );

  const textField = (label: string, key: 'partNumberFilter' | 'srnFilter' | 'ncrIdFilter', placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={values[key]}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<NCRFiltersState>)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
        className={inputCls}
      />
    </div>
  );

  const selectField = (label: string, key: 'statusFilter' | 'liabilityFilter' | 'warehouseFilter', options: ReadonlyArray<Option>, placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={values[key]}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<NCRFiltersState>)}
        className={inputCls}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const searchableField = (
    label: string,
    key: 'siteFilter' | 'supplierCountryFilter' | 'supplierGroupFilter' | 'supplierFilter',
    options: Option[],
    placeholder: string,
    extra?: Partial<NCRFiltersState>,
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <SearchableSelect
        options={options}
        value={values[key]}
        onChange={(val) => onChange({ [key]: val, ...(extra || {}) } as Partial<NCRFiltersState>)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range — the quick ranges and the NCR date pair */}
      <FilterSection title="Date Range" icon={Calendar}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {quickRanges.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              aria-pressed={activeQuickRange === id}
              onClick={() => onQuickRange(id)}
              className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
                activeQuickRange === id
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={fourUpCls}>
          {dateField('Date From', 'dateFrom')}
          {dateField('Date To', 'dateTo')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company */}
      <FilterSection title="Company" icon={Building2}>
        <div className={fourUpCls}>
          {searchableField('Site', 'siteFilter', siteOptions, 'All Sites')}
          {fv.warehouse && selectField('Warehouse', 'warehouseFilter', warehouseOptions, 'All Warehouses')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — Country and Group both narrow Supplier Site */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={fourUpCls}>
          {searchableField('Country', 'supplierCountryFilter', supplierCountryOptions, 'All Countries', { supplierFilter: '' })}
          {searchableField('Group', 'supplierGroupFilter', supplierGroupOptions, 'All Supplier Groups', { supplierFilter: '' })}
          {searchableField('Site', 'supplierFilter', supplierSiteOptions, 'All Supplier Sites')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* NCR Details */}
      <FilterSection title="NCR Details" icon={FileText}>
        <div className={gridCls}>
          {selectField('Status', 'statusFilter', statusOptions, 'All Statuses')}
          {textField('Part Number', 'partNumberFilter', 'Part Number...')}
          {/* E4: a supplier's register mixes customers, so the generic name. */}
          {textField('Customer NCR No.', 'srnFilter', 'Customer NCR No.')}
          {textField('NCR ID', 'ncrIdFilter', 'NCR ID...')}
          {selectField('Liability', 'liabilityFilter', liabilityOptions, 'All Liabilities')}
        </div>
        {/* CR-6451: Divider before Requirements to match customer view */}
        <div className="-mx-2 mt-4 mb-3 border-t border-border" />
        <div>
          <span className={labelCls}>Requirements</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {REQUIREMENTS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(values[key])}
                  onChange={(e) => onChange({ [key]: e.target.checked } as Partial<NCRFiltersState>)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
